import { createContext, useContext, useEffect, useState, useCallback } from "react";

// Theme palette definitions. Keys map to the theme IDs in OmniStore.
// Each theme overrides CSS variables on <html>; "default" reverts to the brand.
const THEMES = {
  default: {
    name: "OmniSearch (par défaut)",
    vars: {
      "--omni-bg": "#ffffff",
      "--omni-surface": "#f9f9f9",
      "--omni-border": "#eaeaea",
      "--omni-text": "#0a0a0a",
      "--omni-text-soft": "#525252",
      "--omni-accent": "#ff4500",
      "--omni-accent-hover": "#e03e00",
    },
    bodyClass: "",
  },
  "theme-midnight": {
    name: "Midnight",
    vars: {
      "--omni-bg": "#0f0c29",
      "--omni-surface": "#1c1849",
      "--omni-border": "#2e2a5f",
      "--omni-text": "#e6e3ff",
      "--omni-text-soft": "#a5a0d9",
      "--omni-accent": "#a78bfa",
      "--omni-accent-hover": "#8b5cf6",
    },
    bodyClass: "omni-dark",
  },
  "theme-sunset": {
    name: "Sunset",
    vars: {
      "--omni-bg": "#fff7ed",
      "--omni-surface": "#ffedd5",
      "--omni-border": "#fed7aa",
      "--omni-text": "#431407",
      "--omni-text-soft": "#9a3412",
      "--omni-accent": "#f97316",
      "--omni-accent-hover": "#ea580c",
    },
    bodyClass: "",
  },
  "theme-forest": {
    name: "Forest",
    vars: {
      "--omni-bg": "#f0fdf4",
      "--omni-surface": "#dcfce7",
      "--omni-border": "#bbf7d0",
      "--omni-text": "#052e16",
      "--omni-text-soft": "#166534",
      "--omni-accent": "#15803d",
      "--omni-accent-hover": "#14532d",
    },
    bodyClass: "",
  },
  "theme-cyber": {
    name: "Cyberpunk",
    vars: {
      "--omni-bg": "#0a0014",
      "--omni-surface": "#1a0033",
      "--omni-border": "#4c1d95",
      "--omni-text": "#fce7f3",
      "--omni-text-soft": "#f9a8d4",
      "--omni-accent": "#ec4899",
      "--omni-accent-hover": "#db2777",
    },
    bodyClass: "omni-dark",
  },
  "theme-paper": {
    name: "Paper",
    vars: {
      "--omni-bg": "#faf6ef",
      "--omni-surface": "#f0e9d8",
      "--omni-border": "#d4c5a0",
      "--omni-text": "#2a1810",
      "--omni-text-soft": "#5c4a36",
      "--omni-accent": "#78716c",
      "--omni-accent-hover": "#57534e",
    },
    bodyClass: "",
  },
  "theme-monochrome": {
    name: "Mono Black",
    vars: {
      "--omni-bg": "#0a0a0a",
      "--omni-surface": "#1a1a1a",
      "--omni-border": "#404040",
      "--omni-text": "#fafafa",
      "--omni-text-soft": "#a3a3a3",
      "--omni-accent": "#ffffff",
      "--omni-accent-hover": "#e5e5e5",
    },
    bodyClass: "omni-dark",
  },
};

const STORAGE_KEY = "omni:theme";
const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => {
    if (typeof window === "undefined") return "default";
    return localStorage.getItem(STORAGE_KEY) || "default";
  });

  const apply = useCallback((id) => {
    const t = THEMES[id] || THEMES.default;
    const root = document.documentElement;
    Object.entries(t.vars).forEach(([k, v]) => root.style.setProperty(k, v));
    // body class for dark variants
    document.body.classList.remove("omni-dark");
    if (t.bodyClass) document.body.classList.add(t.bodyClass);
    // tailwind dark utility
    if (t.bodyClass === "omni-dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, []);

  useEffect(() => {
    apply(themeId);
    localStorage.setItem(STORAGE_KEY, themeId);
  }, [themeId, apply]);

  const setTheme = useCallback((id) => {
    if (!THEMES[id]) id = "default";
    setThemeId(id);
  }, []);

  const reset = useCallback(() => setThemeId("default"), []);

  return (
    <ThemeContext.Provider value={{ themeId, theme: THEMES[themeId] || THEMES.default, themes: THEMES, setTheme, reset }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const THEME_IDS = Object.keys(THEMES);
